<script setup lang="ts">
import { ref, watch } from "vue";
import Dragger from "./Dragger.vue";
import ToolOptionsDropdown from "./toolbar/ToolOptionsDropdown.vue";
import TextInputMenu from "./TextInputMenu.vue";
import DisableToolBtn from "./options/DisableToolBtn.vue";
import CanvasButton from "@/components/canvas/CanvasButton.vue";
import { DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import Settings from "@/stores/Settings";
import { useTextPanelCollapse } from "@/composable/useTextPanelCollapse";

// L'ecriture manuscrite et le clavier stylet sont des variantes de l'outil
// texte : on les active depuis son panneau d'options, qui doit donc rester
// ouvert tant que l'un d'eux est actif.
const TEXT_FAMILY = [
  DraggerType.Text,
  DraggerType.Handwriting,
  DraggerType.StylusKeyboard,
];

const dropdown = ref<InstanceType<typeof ToolOptionsDropdown>>();
const textMenu = ref<InstanceType<typeof TextInputMenu>>();

// Prendre l'outil texte ouvre directement l'éditeur si aucun texte n'est
// en attente. Un re-clic sur l'outil déjà actif ne rouvre rien (le champ du
// panneau est là pour ça).
function activateTextDragger() {
  const wasActive = DraggerMenuState.activeDragger.name === DraggerType.Text;
  DraggerMenuState.setActiveDragger({ name: DraggerType.Text });
  if (!wasActive) {
    textMenu.value?.openEditorIfEmpty();
  }
}

// Panneau texte réduit : masqué en CSS plutôt que fermé, pour qu'il reste
// l'état "ouvert" de bootstrap et reparaisse tel quel. À l'agrandissement on
// le rouvre quand même (un appui sur l'outil a pu le fermer entre-temps) et
// on laisse popper le recaler, sa position ayant été calculée masquée.
const {
  isTextToolActive,
  isCollapsed: isTextPanelCollapsed,
  collapse: collapseTextPanel,
} = useTextPanelCollapse();

watch(isTextPanelCollapsed, async (collapsed) => {
  if (collapsed) return;
  if (!TEXT_FAMILY.includes(DraggerMenuState.activeDragger.name)) return;
  dropdown.value?.show();
  await dropdown.value?.update();
});
</script>

<template>
  <!-- Classe de réduction sur le conteneur : bootstrap écrit .show sur le
       menu lui-même, qu'un :class de Vue effacerait en le re-rendant. -->
  <ToolOptionsDropdown
    ref="dropdown"
    :owners="TEXT_FAMILY"
    :class="{ 'text-tool-panel-collapsed': isTextPanelCollapsed }"
  >
    <template #tool>
      <Dragger
        :config="{ name: DraggerType.Text }"
        phosphor-icon="text-t"
        :tooltip-text="$t('canvas:tools.text.tooltip.text')"
        :tooltip-title="$t('canvas:tools.text.tooltip.title')"
        @click="activateTextDragger()"
      />
    </template>
    <!-- Panneau de l'outil texte : saisie et rappel du geste -->
    <div
      class="rounded p-2 text-tool-panel"
      style="background-color: var(--canvas-bg)"
    >
      <div class="text-tool-actions d-flex flex-row gap-1">
        <!-- Réduire : le champ seul passe en bas à gauche (TextToolDock) -->
        <CanvasButton
          v-if="isTextToolActive"
          size="sm"
          phosphor-icon="arrows-in-simple"
          :tooltip-title="$t('canvas:tools.text.collapse.title')"
          :tooltip-text="$t('canvas:tools.text.collapse.text')"
          @click.prevent.stop="collapseTextPanel"
        />
        <DisableToolBtn v-if="!Settings.onlyStylusOn" size="sm" />
      </div>
      <TextInputMenu ref="textMenu" />
    </div>
  </ToolOptionsDropdown>
</template>

<style>
/**
Panneau de l'outil texte : reduire et la croix de desactivation vivent dans le
coin haut droit, au-dessus de la ligne de titre du panneau (cf.
.text-tool-header).
*/
.text-tool-panel {
  position: relative;
}
.text-tool-actions {
  position: absolute;
  top: 6px;
  right: 6px;
}
/* Reduit : bootstrap le tient toujours ouvert (.show), on le masque par-dessus. */
.text-tool-panel-collapsed > .dr-option-menu {
  display: none !important;
}
</style>
